import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import BackButton from 'components/BackButton';
import { useUserProfile } from 'contexts/UserProfileContext';
import { createAPIEndPoint } from 'config/api/api';
import TimeZoneSelect from 'components/time-zone-select';
import { getUserData } from 'utils';
import toast from 'react-hot-toast';

function ProfileForm() {
    const { getUser } = useUserProfile();
    const [loading, setLoading] = useState(true);
    const userData = getUserData();
    const userId = userData?.id ?? null;

    const textColor = useColorModeValue('navy.700', 'white');
    const [user, setUser] = useState({});

    const fetchUser = async () => {
        try {
            const response = await createAPIEndPoint('user').fetchById(`/${userId}`);
            setUser(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching user:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUser();
    }, [userId]);

    const updateProfile = async (profileData) => {
        try {
            await createAPIEndPoint('user/').update(userId, profileData);
            getUser();
            fetchUser();
            toast.success("User updated successfully!")
        } catch (error) {
            throw error?.response?.data?.message || 'Profile update failed. Please try again.';
        }
    };

    const formatPhoneNumber = (value) => {
        const cleaned = value.replace(/\D/g, ''); // Remove non-numeric characters
        const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

        if (!match) return value;

        const formatted = !match[2] ? match[1] : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;

        return formatted.length > 14 ? formatted.slice(0, 14) : formatted; // Limit to 14 characters
    };


    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            address: user.address || '',
            phone: user.phone || '',
            timezone: user.timezone || '',
            enable2FA: false,
        },
        validationSchema: Yup.object().shape({
            first_name: Yup.string().max(255).required('First name is required'),
            last_name: Yup.string().max(255).required('Last name is required'),
            address: Yup.string().max(255).required('Address is required'),
            phone: Yup.string()
                .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone number must be in format (XXX) XXX-XXXX')
                .required('Phone number is required'),
            timezone: Yup.string().required('Time Zone is required'),
        }),
        onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
            try {
                setLoading(true);
                await updateProfile(values);
                resetForm();
            } catch (error) {
                setErrors({ submit: error });
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        },
    });

    return (
        <Box pt={{ base: '80px', md: '80px', xl: '80px' }}>
            <BackButton />
            <Flex align="center" justify="center">
                <Box p={6} borderRadius="lg" boxShadow="lg" bg="white" w="600px">
                    <Heading color={textColor} fontSize={{ base: '24px', md: '28px', lg: '32px' }} mb="10px">
                        Profile
                    </Heading>
                    <form onSubmit={formik.handleSubmit}>
                        <FormControl isInvalid={formik.touched.first_name && formik.errors.first_name} mb={4}>
                            <FormLabel>First Name</FormLabel>
                            <Input name="first_name" placeholder="Enter your first name"
                                onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.first_name} />
                            {formik.touched.first_name && formik.errors.first_name && <Text color="red.500" fontSize="sm">{formik.errors.first_name}</Text>}
                        </FormControl>

                        <FormControl isInvalid={formik.touched.last_name && formik.errors.last_name} mb={4}>
                            <FormLabel>Last Name</FormLabel>
                            <Input name="last_name" placeholder="Enter your last name"
                                onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.last_name} />
                            {formik.touched.last_name && formik.errors.last_name && <Text color="red.500" fontSize="sm">{formik.errors.last_name}</Text>}
                        </FormControl>

                        <FormControl isInvalid={formik.touched.address && formik.errors.address} mb={4}>
                            <FormLabel>Address</FormLabel>
                            <Input name="address" placeholder="Enter your address"
                                onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.address} />
                            {formik.touched.address && formik.errors.address && <Text color="red.500" fontSize="sm">{formik.errors.address}</Text>}
                        </FormControl>

                        <FormControl isInvalid={formik.touched.phone && formik.errors.phone} mb={4}>
                            <FormLabel>Phone Number</FormLabel>
                            <Input
                                name="phone"
                                placeholder="(XXX) XXX-XXXX"
                                maxLength={14}
                                onChange={(e) =>
                                    formik.handleChange({
                                        target: { name: 'phone', value: formatPhoneNumber(e.target.value) }
                                    })
                                } onBlur={formik.handleBlur} value={formik.values.phone} />
                            {formik.touched.phone && formik.errors.phone && <Text color="red.500" fontSize="sm">{formik.errors.phone}</Text>}
                        </FormControl>

                        <TimeZoneSelect
                            values={formik.values}
                            handleChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            touched={formik.touched}
                            errors={formik.errors}
                        />

                        {/* <FormControl mb={4}>
                            <Checkbox name="enable2FA" onChange={formik.handleChange} isChecked={formik.values.enable2FA}>
                                Enable 2FA
                            </Checkbox>
                        </FormControl> */}

                        <Button type="submit" colorScheme="blue" width="full" isLoading={loading}>Submit</Button>
                    </form>
                </Box>
            </Flex>
        </Box>
    );
}

export default ProfileForm;